import React, { useEffect, useState } from 'react';
import { getDashboard } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import { 
    DollarSign, 
    Users, 
    Wallet, 
    AlertTriangle, 
    TrendingUp,
    TrendingDown,
    Calendar,
    Receipt
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const COLORS = ['hsl(161, 91%, 16%)', 'hsl(160, 84%, 39%)', 'hsl(28, 87%, 37%)', 'hsl(0, 72%, 42%)'];

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'primary' }) => (
    <Card className="card-hover animate-fadeIn">
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-muted-foreground font-medium">{title}</p>
                    <p className="text-2xl font-heading font-bold mt-1">{value}</p>
                    {trend && (
                        <div className={`flex items-center gap-1 mt-2 text-sm ${
                            trend === 'up' ? 'text-accent' : 'text-destructive'
                        }`}>
                            {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            <span>{trendValue}</span>
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-lg ${
                    color === 'primary' ? 'bg-primary/10 text-primary' :
                    color === 'accent' ? 'bg-accent/10 text-accent' :
                    color === 'warning' ? 'bg-warning/10 text-warning' :
                    'bg-destructive/10 text-destructive'
                }`}>
                    <Icon className="w-6 h-6" />
                </div>
            </div>
        </CardContent>
    </Card>
);

export default function DashboardPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            const { data: dashboardData } = await getDashboard();
            setData(dashboardData);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton className="h-80" />
                    <Skeleton className="h-80" />
                </div>
            </div>
        );
    }

    const frecuenciaData = data?.por_frecuencia 
        ? Object.entries(data.por_frecuencia).map(([name, values]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            cantidad: values.count,
            monto: values.monto
        }))
        : [];

    const ingresosData = [
        { name: 'Capital', value: data?.ingresos_mes?.capital || 0 },
        { name: 'Interés', value: data?.ingresos_mes?.interes || 0 },
        { name: 'Mora', value: data?.ingresos_mes?.mora || 0 },
    ].filter(item => item.value > 0);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                        Hola, {user?.nombre?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Resumen de tu cartera de préstamos
                    </p>
                </div>
                <Badge variant="outline" className="w-fit">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date().toLocaleDateString('es-DO', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}
                </Badge>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Prestado"
                    value={formatCurrency(data?.cartera?.total_prestado || 0)}
                    icon={DollarSign}
                    color="primary"
                />
                <StatCard
                    title="Total Cobrado"
                    value={formatCurrency(data?.cartera?.total_cobrado || 0)}
                    icon={Receipt}
                    trend="up"
                    trendValue={`${data?.cartera?.porcentaje_recuperacion || 0}% recuperado`}
                    color="accent"
                />
                <StatCard
                    title="Mora Acumulada"
                    value={formatCurrency(data?.mora?.total_mora || 0)}
                    icon={AlertTriangle}
                    trend={data?.mora?.prestamos_en_mora > 0 ? 'down' : undefined}
                    trendValue={`${data?.mora?.prestamos_en_mora || 0} préstamos`}
                    color="destructive"
                />
                <StatCard
                    title="Clientes Activos"
                    value={data?.conteos?.clientes || 0}
                    icon={Users}
                    color="primary"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Trend Chart */}
                <Card className="animate-fadeIn stagger-1">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Cobranza Últimos 7 Días</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64" style={{ minHeight: '250px' }}>
                            <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                                <AreaChart data={data?.tendencia || []}>
                                    <defs>
                                        <linearGradient id="colorMonto" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="hsl(161, 91%, 16%)" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="hsl(161, 91%, 16%)" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 85%)" />
                                    <XAxis 
                                        dataKey="fecha" 
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('es-DO', { weekday: 'short' })}
                                        stroke="hsl(240, 4%, 46%)"
                                        fontSize={12}
                                    />
                                    <YAxis 
                                        tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                                        stroke="hsl(240, 4%, 46%)"
                                        fontSize={12}
                                    />
                                    <Tooltip 
                                        formatter={(value) => [formatCurrency(value), 'Cobrado']}
                                        labelFormatter={(label) => new Date(label).toLocaleDateString('es-DO')}
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid hsl(240, 6%, 85%)',
                                            borderRadius: '8px',
                                        }}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="monto" 
                                        stroke="hsl(161, 91%, 16%)" 
                                        fillOpacity={1}
                                        fill="url(#colorMonto)"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Income Distribution */}
                <Card className="animate-fadeIn stagger-2">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Ingresos del Mes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64 flex items-center justify-center" style={{ minHeight: '250px' }}>
                            {ingresosData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                                    <PieChart>
                                        <Pie
                                            data={ingresosData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={90}
                                            paddingAngle={4}
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {ingresosData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted-foreground">Sin ingresos este mes</p>
                            )}
                        </div>
                        <div className="mt-4 text-center">
                            <p className="text-sm text-muted-foreground">Total del Mes</p>
                            <p className="text-2xl font-heading font-bold text-primary">
                                {formatCurrency(data?.ingresos_mes?.total || 0)}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Frequency Chart */}
            <Card className="animate-fadeIn stagger-3">
                <CardHeader>
                    <CardTitle className="font-heading text-lg">Préstamos por Frecuencia</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-64" style={{ minHeight: '250px' }}>
                        <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={250}>
                            <BarChart data={frecuenciaData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 85%)" />
                                <XAxis dataKey="name" stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                <YAxis yAxisId="left" stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                <YAxis 
                                    yAxisId="right" 
                                    orientation="right" 
                                    tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                                    stroke="hsl(240, 4%, 46%)" 
                                    fontSize={12}
                                />
                                <Tooltip 
                                    formatter={(value, name) => [
                                        name === 'monto' ? formatCurrency(value) : value,
                                        name === 'monto' ? 'Monto Total' : 'Cantidad'
                                    ]}
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid hsl(240, 6%, 85%)',
                                        borderRadius: '8px',
                                    }}
                                />
                                <Legend />
                                <Bar yAxisId="left" dataKey="cantidad" fill="hsl(161, 91%, 16%)" name="Cantidad" radius={[4, 4, 0, 0]} />
                                <Bar yAxisId="right" dataKey="monto" fill="hsl(160, 84%, 39%)" name="Monto" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="animate-fadeIn stagger-4">
                    <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">Préstamos Activos</p>
                        <p className="text-3xl font-heading font-bold text-primary mt-1">
                            {data?.conteos?.prestamos_activos || 0}
                        </p>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-4">
                    <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">En Mora</p>
                        <p className="text-3xl font-heading font-bold text-destructive mt-1">
                            {data?.mora?.prestamos_en_mora || 0}
                        </p>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-5">
                    <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">% Mora</p>
                        <p className="text-3xl font-heading font-bold text-warning mt-1">
                            {data?.mora?.porcentaje_mora || 0}%
                        </p>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-5">
                    <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">Saldo Pendiente</p>
                        <p className="text-xl font-heading font-bold text-foreground mt-1">
                            {formatCurrency(data?.cartera?.saldo_pendiente || 0)}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
